/* eslint-disable prefer-object-spread */
// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import FancyScroll, { FancyScrollRefProps } from "@flip/App/Components/Common/FancyScroll";
import { TippyPopupStyle } from "@flip/App/Components/Tippy/Style";
import TippyPopup from "@flip/App/Components/Tippy/TippyPopup";
import headerSearchInputStyles from "@flip/App/SharedStyle/HeaderSearchInputStyles";
import { CustomAppState } from "@flip/App/Store/Reducers";
import { xIsAuthenticated } from "@flip/App/Store/Selectors/GeneralSelectors";
import { FormFieldClickable } from "@flip/MobiusOverrides/FormField/FormField";
import { sizeVariantValues } from "@flip/MobiusOverrides/FormField/formStyles";
import TextField, { TextFieldPresentationProps } from "@flip/MobiusOverrides/TextField";
import AutocompleteBrands, { AutocompleteBrandsStyles } from "@flip/Overrides/Widgets/Header/AutocompleteBrands";
import AutocompleteCategories, {
    AutocompleteCategoriesStyles,
} from "@flip/Overrides/Widgets/Header/AutocompleteCategories";
import AutocompleteProducts, { AutocompleteProductsStyles } from "@flip/Overrides/Widgets/Header/AutocompleteProducts";
import SearchHistory, { SearchHistoryStyles } from "@flip/Overrides/Widgets/Header/SearchHistory";
import Search from "@flip/XDL/Icons/Search";
import { color, mergeWithCss, parseBoolean, styledScrollbar, xScreen } from "@flip/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import {
    addSearchHistory,
    getSearchHistory,
    SearchHistoryItem,
} from "@insite/client-framework/Services/AutocompleteService";
import getAutocompleteModel from "@insite/client-framework/Store/CommonHandlers/GetAutocompleteModel";
import sendSearchTracking from "@insite/client-framework/Store/CommonHandlers/SendSearchTracking";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { idNotFound } from "@insite/client-framework/Store/Data/DataState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AutocompleteItemModel,
    AutocompleteModel,
    ProductModel,
    UnitOfMeasureModel,
} from "@insite/client-framework/Types/ApiModels";
import AutocompleteContent, {
    AutocompleteContentStyles,
} from "@insite/content-library/Widgets/Header/AutocompleteContent";
import { FormFieldSizeVariant } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import { IconWrapper } from "@insite/mobius/Icon";
import {
    ContentBodyProps,
    OverflowWrapperProps,
    PopoverPresentationProps,
    PositionStyle,
} from "@insite/mobius/Popover";
import { InputStyle } from "@insite/mobius/TextField";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import debounce from "lodash/debounce";
import throttle from "lodash/throttle";
import React, { createRef } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";
// eslint-disable-next-line spire/fenced-imports
import { Instance as TippyInstance } from "tippy.js";

interface OwnProps {
    /** A unique identifier to assist with id-generation for `aria-controls`. */
    readonly id: string;
    inputRef?: React.Ref<HTMLInputElement>;
    autocompletePositionFunction?: (element: React.RefObject<HTMLUListElement>) => PositionStyle;
    onBeforeGoToUrl?: () => void;
    extendedStyles?: SearchInputStyles;
    isAnimated?: boolean;
    // XNG-Changes
    left?: number;
    top?: number;
    hideOverlay?: boolean;
}

const mapStateToProps = (state: CustomAppState) => {
    const {
        searchSettings: { autocompleteEnabled, searchHistoryEnabled, searchHistoryLimit, searchPath },
    } = getSettingsCollection(state);
    return {
        autocompleteEnabled,
        searchHistoryEnabled,
        searchHistoryLimit,
        location: getLocation(state),
        searchPath,
        isTabletScreen: state.xCommon.isTabletScreen,
        isMobileScreen: state.xCommon.isMobileScreen,
        isSmallMobileScreen: state.xCommon.isSmallMobileScreen,
        isAuthenticated: xIsAuthenticated(state),
    };
};

const mapDispatchToProps = {
    getAutocompleteModel,
    sendSearchTracking,
};

type Props = OwnProps &
    ThemeProps<BaseTheme> &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

interface State {
    query: string;
    focusableAutocompleteItems: AutocompleteItemModel[];
    focusedAutocompleteItem?: AutocompleteItemModel;
    focusedSearchHistoryQuery?: string;
    autoCompleteModel?: AutocompleteModel;
    isLoading: boolean;
    // XNG-Changes
    tippyInstance?: TippyInstance;
}

// XNG-Changes
const DOWN_KEY = "ArrowDown";
const UP_KEY = "ArrowUp";
const ENTER_KEY = "Enter";

export interface SearchInputStyles {
    input?: TextFieldPresentationProps;
    popover?: PopoverPresentationProps;
    popoverWrapper?: OverflowWrapperProps;
    popoverContentBody?: ContentBodyProps;
    autocompleteWrapper?: InjectableCss;
    autocompleteColumnWrapper?: InjectableCss;
    searchHistoryStyles?: SearchHistoryStyles;
    autocompleteCategoriesStyles?: AutocompleteCategoriesStyles;
    autocompleteBrandsStyles?: AutocompleteBrandsStyles;
    autocompleteContentStyles?: AutocompleteContentStyles;
    autocompleteProductsStyles?: AutocompleteProductsStyles;
    // XNG-Changes
    autocompleteColumnProductWrapper?: InjectableCss;
    inputWrapper?: InjectableCss;
    tippyPopup?: TippyPopupStyle;
}

export const searchInputStyles: SearchInputStyles = {
    input: {
        sizeVariant: "default",
        iconProps: { src: Search, size: 12 },
        iconClickableProps: {
            iconPosition: "left",
            typographyProps: {
                size: 14,
                lineHeight: "16px",
                css: css`
                    letter-spacing: 0;
                `,
            },
            css: css`
                ${IconWrapper}, svg {
                    height: 12px;
                    width: 12px;
                    justify-content: center;
                    color: ${color("secondary")};
                }
            `,
        },
        cssOverrides: {
            formInputWrapper: css`
                && {
                    ${InputStyle},${InputStyle}:focus {
                        padding-left: 28px;

                        ${xScreen("lt", "sm")} {
                            height: 36px;
                            padding-left: 30px;
                        }
                    }

                    ${FormFieldClickable} {
                        width: 34px;
                    }
                }
            `,
        },
    },
    popover: {
        transitionDuration: "regular",
        toggle: false,
    },
    popoverWrapper: {
        as: "div",
        _height: "auto",
        css: css`
            display: inline-flex;
            justify-content: flex-end;
        `,
    },
    popoverContentBody: {
        as: "div",
        _height: "100%",
    },
    // XNG-Changes: FLIP-977
    autocompleteWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            width: ${({ width }: any) => width}px;
            ${xScreen("lte", "md")} {
                max-height: 400px;
                width: ${({ width }: any) => width - 2}px;
            }

            ${({ isAuthenticated }: any) => css`
                ${isAuthenticated
                    ? css`
                          ${xScreen("lte", "xs")} {
                              max-height: unset;
                          }
                      `
                    : css`
                          ${xScreen("lte", "xs")} {
                              max-height: 487px;
                          }
                      `};
            `}
            ${({ hideOverlay }: any) => css`
                ${hideOverlay
                    ? css`
                          ${xScreen("lte", "md")} {
                              flex-direction: column;
                          }
                      `
                    : css`
                          ${xScreen("lte", "xs")} {
                              flex-direction: column;
                          }
                      `};
            `}
        `,
    },
    autocompleteColumnWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            position: relative;
            ${xScreen("gt", "md")} {
                ${styledScrollbar(4)}
            }
            /* XNG-Changes: FLIP-813 */
            /* &:after {
                    content: "";
                    background: ${color("common.ui4")};
                    width: 1px;
                    height: 100%;
                    position: absolute;
                    right: 0;
                    top: 0;
                } */
            ${({ hideOverlay }: any) => css`
                ${hideOverlay
                    ? css`
                          width: 290px;
                          min-width: 290px;
                          ${xScreen("lte", "md")} {
                              width: 100%;
                              min-width: initial;
                              min-height: unset;
                              padding-bottom: 4px;
                              &:after {
                                  display: none;
                              }
                          }
                      `
                    : css`
                          width: 432px;
                          min-width: 432px;
                          min-height: 484px;
                          ${xScreen("lte", "md")} {
                              width: 243px;
                              min-width: 243px;
                          }

                          ${xScreen("lte", "xs")} {
                              width: 100%;
                              min-width: initial;
                              min-height: unset;
                              padding-bottom: 4px;
                              &:after {
                                  display: none;
                              }
                          }
                      `};
            `}
        `,
    },
    /* XNG-Changes: FLIP-813 */
    autocompleteColumnProductWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            width: 100%;
            padding: 16px 22px;
            ${xScreen("lte", "xs")} {
                width: 100%;
            }
            ${xScreen("gt", "md")} {
                height: 100%;
            }
            ${xScreen("gte", "sm")} {
                border-left: 1px solid ${color("common.ui5")};
            }
        `,
    },
    autocompleteCategoriesStyles: {
        headerText: {
            ...headerSearchInputStyles?.headerText,
        },
        link: {
            ...headerSearchInputStyles?.link,
        },
        focusedCategoryItemText: {
            ...headerSearchInputStyles?.focusedItemText,
        },
        focusedLink: {
            ...headerSearchInputStyles?.focusedLink,
        },
        categoryItemText: {
            ...headerSearchInputStyles?.itemText,
        },
        itemWrapper: {
            ...headerSearchInputStyles?.itemWrapper,
        },
    },
    autocompleteBrandsStyles: {
        headerText: {
            ...headerSearchInputStyles?.headerText,
        },
        link: {
            ...mergeWithCss(headerSearchInputStyles?.link, headerSearchInputStyles?.itemWrapper),
        },
        focusedBrandItemText: {
            ...headerSearchInputStyles?.focusedItemText,
        },
        focusedLink: {
            ...mergeWithCss(headerSearchInputStyles?.focusedLink, headerSearchInputStyles?.itemWrapper),
        },
        brandItemText: {
            ...headerSearchInputStyles?.itemText,
        },
    },
    autocompleteContentStyles: {
        headerText: {
            ...headerSearchInputStyles?.headerText,
        },
        link: {
            ...mergeWithCss(headerSearchInputStyles?.link, headerSearchInputStyles?.itemWrapper),
        },
        focusedLink: {
            ...mergeWithCss(headerSearchInputStyles?.focusedLink, headerSearchInputStyles?.itemWrapper),
        },
    },
    inputWrapper: {
        css: css`
            line-height: 0; // to fix extra spacing on firefox
            position: relative;
        `,
    },
    // XNG-Changes: FLIP-813
    tippyPopup: {
        main: {
            css: css`
                &.tippy-box {
                    border: 1px solid ${color("common.ui5")};
                    border-top: none;
                }
            `,
        },
    },
};

const styles = searchInputStyles;

class SearchInput extends React.Component<Props, State> {
    private readonly styles: SearchInputStyles;

    // XNG-Changes
    private parentMenu = React.createRef<HTMLDivElement>();
    private inputWrapperRef = createRef<HTMLDivElement>();
    private fancyScrollRef = createRef<FancyScrollRefProps>();

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);

        this.state = {
            query: "",
            focusableAutocompleteItems: [],
            isLoading: false,
            // XNG-Changes
            tippyInstance: undefined,
        };
    }

    list = React.createRef<HTMLUListElement>();
    popover = React.createRef<any>();
    showAutocomplete = true;

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.props.location.pathname !== prevProps.location.pathname) {
            // clear the search box on navigate away from search
            if (!this.props.location.pathname.toLowerCase().startsWith(`/${this.props.searchPath.toLowerCase()}`)) {
                // TODO ISC-12679 - Redesign so that this can be moved to a field of the class rather than state.
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ query: "" });

                // <XNG-Changes>
                // OOTB issue, when a user do search and return back to home page, the history not showAutocomplete update so the history popup not appear
                this.showAutocomplete = true;
                // </XNG-Changes>
            } else {
                this.closeAutocomplete();
            }
        }
        if (
            this.state.autoCompleteModel &&
            prevState.autoCompleteModel &&
            this.state.autoCompleteModel !== prevState.autoCompleteModel
        ) {
            this.updateFocusableItems();
        }
    }

    componentDidMount() {
        window.addEventListener("resize", this.handleResize, true);
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.handleResize);
    }

    handleResize = () => {
        this.updateFocusableItems();
    };

    updateFocusableItems = () => {
        if (this.state.isLoading || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const isProductsOnTop = window.innerWidth < this.props.theme.breakpoints.values[3];
        const newFocusableOptions = isProductsOnTop
            ? (products || ([] as AutocompleteItemModel[]))
                  .concat(categories || [])
                  .concat(brands || [])
                  .concat(content || [])
            : (categories || [])
                  .concat(brands || [])
                  .concat(content || [])
                  .concat(products || []);
        this.setState({
            focusableAutocompleteItems: newFocusableOptions,
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    doSearch = () => {
        if (this.state.query.trim() === "") {
            return;
        }

        this.showAutocomplete = false;
        if (this.state.autoCompleteModel?.products?.length === 1) {
            this.goToUrl(this.state.autoCompleteModel?.products[0].url);
        } else {
            const encodedQuery = encodeURIComponent(this.state.query);
            this.goToUrl(`/${this.props.searchPath}?query=${encodedQuery}`, false);
        }
        if (this.props.searchHistoryEnabled) {
            addSearchHistory(this.state.query, this.props.searchHistoryLimit);
        }
    };

    // XNG-Changes: fix empty popup
    clearSearchQuery = () => {
        this.showAutocomplete = true;
        this.setState({ query: "" });
    };

    loadAutocomplete = debounce((query: string) => {
        this.props.getAutocompleteModel({
            query,
            onSuccess: result => {
                if (this.showAutocomplete) {
                    this.setState(
                        {
                            autoCompleteModel: result,
                        },
                        () => {
                            this.fancyScrollRef?.current?.updateScrollable();
                        },
                    );
                    // this.openAutocomplete();
                    // <XNG-Changes>
                    this.updatePopoverVisibility(result);
                    // </XNG-Changes>
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    }, 200);

    handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, toggleAutoComplete = true) => {
        this.showAutocomplete = toggleAutoComplete;
        const newQuery = event.target.value;
        this.setState({ query: newQuery });
        this.loadAutocomplete(newQuery);

        // XNG-Changes
        this.updatePopoverVisibility(this.state.autoCompleteModel);
    };

    openAutocomplete = () => {
        // XNG-Changes
        this.state.tippyInstance?.show();
    };

    closeAutocomplete = () => {
        // XNG-Changes
        this.state.tippyInstance?.hide();
    };

    // XNG-Changes: Check if popeover is opened
    isPopoverVisible(results: AutocompleteModel | undefined) {
        let isVisible: boolean | null = false;
        if (this.state.query && this.state.query.length > 2 && results) {
            const { categories, brands, content, products } = results;
            isVisible =
                this.props.autocompleteEnabled &&
                ((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0) ||
                    (products && products.length > 0));
        } else if (!this.state.query) {
            const searchHistoryItems = getSearchHistory();
            isVisible = this.props.searchHistoryEnabled && searchHistoryItems && searchHistoryItems.length > 0;
        }
        return isVisible;
    }

    // XNG-Changes
    updatePopoverVisibility(results: AutocompleteModel | undefined) {
        if (this.isPopoverVisible(results)) {
            this.openAutocomplete();
        } else {
            this.closeAutocomplete();
        }
    }

    // XNG-Changes
    handleInputFocus = () => {
        this.updatePopoverVisibility(this.state.autoCompleteModel);
    };

    // XNG-Changes
    clearSearchHistoryCallback = () => {
        this.updatePopoverVisibility(undefined);
    };

    getPopoverPosition = (element: React.RefObject<HTMLUListElement>) => {
        if (this.props.autocompletePositionFunction) {
            return this.props.autocompletePositionFunction(element);
        }

        const positionStyle: PositionStyle = {
            position: "fixed",
        };

        if (element.current) {
            const rect = element.current.getBoundingClientRect();
            positionStyle.top =
                rect.top +
                (rect.height > 100
                    ? sizeVariantValues[this.styles?.input?.sizeVariant || ("default" as FormFieldSizeVariant)].height
                    : rect.height);
            positionStyle.right = document.documentElement.clientWidth - rect.right;

            if (this.props.isAnimated) {
                // Adjust for compact header usage
                positionStyle.top += 3;
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderRight) {
                    positionStyle.right -= Number(this.props.theme.header.borderThickness);
                }
                if (this.props.theme.header.isBorder && this.props.theme.header.isBorderTop) {
                    positionStyle.top -= Number(this.props.theme.header.borderThickness);
                }
            }
        }

        return positionStyle;
    };

    moveFocus = throttle((direction: "prev" | "next") => {
        if (this.state.query) {
            this.setState(({ focusedAutocompleteItem, focusableAutocompleteItems }) => {
                const focusableQueue =
                    direction === "prev" ? focusableAutocompleteItems.slice().reverse() : focusableAutocompleteItems;
                const focusedIndex = focusableQueue.indexOf(focusedAutocompleteItem as AutocompleteItemModel);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedAutocompleteItem: newFocusedOption };
            });
        } else {
            this.setState(({ focusedSearchHistoryQuery }) => {
                const searchHistoryItems = getSearchHistory();
                const focusableQueue = direction === "prev" ? searchHistoryItems.slice().reverse() : searchHistoryItems;
                const focusedSearchHistoryItem = focusableQueue.find(o => o.query === focusedSearchHistoryQuery);
                const focusedIndex = focusableQueue.indexOf(focusedSearchHistoryItem as SearchHistoryItem);
                const newFocusedOption = focusableQueue.find((_, index) => index > focusedIndex);
                return { focusedSearchHistoryQuery: newFocusedOption?.query };
            });
        }
    }, 100);

    handlePopoverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.key) {
            case DOWN_KEY:
                this.moveFocus("next");
                break;
            case UP_KEY:
                this.moveFocus("prev");
                break;
            case ENTER_KEY:
                if (this.state.focusedAutocompleteItem) {
                    // XNG-Changes
                    event.currentTarget.blur();
                    this.goToUrl(this.state.focusedAutocompleteItem.url);
                } else if (this.state.focusedSearchHistoryQuery) {
                    // XNG-Changes
                    event.currentTarget.blur();
                    this.setState({ query: this.state.focusedSearchHistoryQuery }, () => {
                        this.doSearch();
                    });
                } else {
                    // XNG-Changes
                    event.currentTarget.blur();
                    this.doSearch();
                }
                break;
            default:
                break;
        }
    };

    handlePopoverClose = () => {
        this.setState({
            focusedAutocompleteItem: undefined,
            focusedSearchHistoryQuery: undefined,
        });
    };

    trackAutcompleteSearchResults = () => {
        // Make sure only to trigger for autocomplete results
        if (!this.state.query || !this.props.autocompleteEnabled || !this.state.autoCompleteModel) {
            return;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        const resultCount =
            (products?.length ?? 0) + (categories?.length || 0) + (content?.length || 0) + (brands?.length || 0);
        if (resultCount > 0) {
            // Track an autocomplete of 1+ items
            this.props.sendSearchTracking({
                searchEvent: "autocompleteSearchResults",
                searchTerm: this.state.query,
                resultCount,
                productCount: products?.length,
                categoryCount: categories?.length,
                contentCount: content?.length,
                brandCount: brands?.length,
            });
            return;
        }

        // Track zero resultCount search results and triggers from this.doSearch
        this.props.sendSearchTracking({
            searchEvent: "searchResults",
            searchTerm: this.state.query,
            resultCount,
        });
    };

    goToUrl = (url: string, track = true) => {
        this.props.onBeforeGoToUrl && this.props.onBeforeGoToUrl();
        if (track) {
            this.trackAutcompleteSearchResults();
        }
        this.closeAutocomplete();
        this.setState({
            // don't use stale results on next search and go to wrong PDP
            autoCompleteModel: undefined,
        });
        this.props.history.push(url);
    };

    renderAutocomplete = (hideOverlay: boolean | undefined, isAuthenticated: boolean | undefined) => {
        if (!this.props.autocompleteEnabled) {
            return null;
        }

        if (!this.state.autoCompleteModel) {
            return null;
        }

        const { categories, brands, content, products } = this.state.autoCompleteModel;
        return (
            <StyledWrapper
                {...this.styles.autocompleteWrapper}
                {...{ width: (this.inputWrapperRef.current?.getBoundingClientRect().width || 300) - 2 }}
                {...{ hideOverlay }}
                {...{ isAuthenticated }}
                className="autocomplete-wrapper"
            >
                {((categories && categories.length > 0) ||
                    (brands && brands.length > 0) ||
                    (content && content.length > 0)) && (
                    <StyledWrapper
                        {...this.styles.autocompleteColumnWrapper}
                        className="autocomplete-column-wrapper"
                        {...{ hideOverlay }}
                    >
                        <AutocompleteCategories
                            categories={categories}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteCategoriesStyles}
                        />
                        <AutocompleteBrands
                            brands={brands}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteBrandsStyles}
                        />
                        <AutocompleteContent
                            content={content}
                            focusedItem={this.state.focusedAutocompleteItem}
                            goToUrl={this.goToUrl}
                            extendedStyles={this.styles.autocompleteContentStyles}
                        />
                    </StyledWrapper>
                )}
                {products && products.length > 0 && (
                    <FancyScroll refElement={this.parentMenu} ref={this.fancyScrollRef}>
                        <StyledWrapper
                            {...this.styles.autocompleteColumnProductWrapper}
                            className="autocomplete-column-wrapper"
                            ref={this.parentMenu}
                        >
                            <AutocompleteProducts
                                products={products}
                                focusedItem={this.state.focusedAutocompleteItem}
                                goToUrl={this.goToUrl}
                                doSearch={this.doSearch}
                                extendedStyles={this.styles.autocompleteProductsStyles}
                            />
                        </StyledWrapper>
                    </FancyScroll>
                )}
            </StyledWrapper>
        );
    };

    // XNG-Changes
    renderSearchAutoCompleteContent = (hideOverlay: boolean | undefined, isAuthenticated: boolean | undefined) => {
        if (!this.state.query && this.showAutocomplete) {
            return (
                <SearchHistory
                    focusedQuery={this.state.focusedSearchHistoryQuery}
                    goToUrl={this.goToUrl}
                    extendedStyles={styles.searchHistoryStyles}
                    width={(this.inputWrapperRef.current?.getBoundingClientRect().width || 300) - 2}
                    clearSearchHistoryCallback={this.clearSearchHistoryCallback}
                />
            );
        }
        if (this.state.query) {
            return this.renderAutocomplete(hideOverlay, isAuthenticated);
        }
    };

    render() {
        const styles = this.styles;
        const searchId = `headerSearch-${this.props.id}`;
        const { left, top } = this.props;

        const searchInput = (
            <StyledWrapper ref={this.inputWrapperRef} {...styles.inputWrapper}>
                <TextField
                    {...styles.input}
                    ref={this.props.inputRef}
                    isAnimated={this.props.isAnimated}
                    // XNG-Changes: merge with iconClickableProps
                    iconClickableProps={{ ...styles.input?.iconClickableProps, onClick: this.doSearch }}
                    value={this.state.query}
                    onChange={this.handleInputChange}
                    placeholder={translate("Search by Part # or Keyword")}
                    autoComplete="off"
                    id={searchId}
                    data-test-selector="headerSearchInputTextField"
                    clickableText={translate("Search")}
                    className="search-input"
                    // XNG-Changes
                    onKeyDown={this.handlePopoverKeyDown}
                    aria-multiline="false"
                    aria-required="false"
                    onFocus={this.handleInputFocus}
                    maxLength={100}
                />
            </StyledWrapper>
        );

        return (
            <>
                <VisuallyHidden as="label" htmlFor={searchId} id={`${searchId}-label`}>
                    {translate("Site Search")}
                </VisuallyHidden>
                {/* XNG-Changes: change popover with tippy, FLIP-662 */}

                <TippyPopup
                    onCreate={instance => {
                        this.setState({ tippyInstance: instance });
                    }}
                    onShow={instance => {
                        this.fancyScrollRef?.current?.updateScrollable();
                        this.setState({ tippyInstance: instance });
                    }}
                    content={
                        <>{this.renderSearchAutoCompleteContent(this.props.hideOverlay, this.props.isAuthenticated)}</>
                    }
                    trigger="manual"
                    offset={this.props.isSmallMobileScreen ? [left || 0, top || 4] : [left || 0, top || 0]}
                    duration={[null, null]}
                    arrow={false}
                    maxWidth="none"
                    placement="bottom-start"
                    type="menu"
                    showCloseButton={false}
                    appendTo={() => document.body}
                    extendedStyles={styles.tippyPopup}
                    hideOverlay={this.props.hideOverlay}
                    popperOptions={{
                        strategy: "absolute",
                        modifiers: [
                            {
                                name: "preventOverflow",
                                options: {
                                    padding: 0,
                                },
                            },
                            {
                                name: "computeStyles",
                                options: {
                                    roundOffsets: ({ x, y }: any) => ({
                                        x,
                                        y,
                                    }),
                                },
                            },
                        ],
                    }}
                >
                    {searchInput}
                </TippyPopup>
            </>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(SearchInput)));
